import { useCallback, useState } from 'react';
import styled from '@emotion/styled';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import type { Chat } from '@trinity/chat';
import { useNavigate } from '@trinity/chat-components/containers/Chat/RoutingProvider';
import { CancelAction } from '../presentation/Message.QueryHint/CancelAction';
import { AnalysisQueryHint } from '../presentation/Message.QueryHint/QueryHint';
import { useCancelSegmentChat } from './hooks/useCancelSegmentChat';

type Props = {
  queryIdentifier: string;
  value: Chat.QueryState.SegmentChatNeeded;
};

export const SegmentChatSelection = (props: Props) => {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const navigate = useNavigate();

  const cancelMutation = useCancelSegmentChat({ queryIdentifier: props.queryIdentifier });

  const handleCheckboxChange = useCallback((id: number, checked: boolean) => {
    setSelectedId(checked ? id : null);
  }, []);

  const handleLaunchDigitalTwin = useCallback(() => {
    if (selectedId === null || !props.value.projectSlug) return;
    navigate(`/projects/${props.value.projectSlug}/hcp-digital-twins?segmentId=${selectedId}`);
  }, [selectedId, props.value.projectSlug, navigate]);

  const handleBrowseAll = useCallback(() => {
    if (!props.value.projectSlug) return;
    navigate(`/projects/${props.value.projectSlug}/hcp-digital-twins`);
  }, [props.value.projectSlug, navigate]);

  const handleCancel = useCallback(() => {
    return cancelMutation.mutateAsync();
  }, [cancelMutation]);

  const selectedSegment = props.value.segments.find((s) => s.id === selectedId);
  const segmentSelected = selectedId !== null;
  const canActOnSegment = segmentSelected && !!props.value.projectSlug && !cancelMutation.isPending;
  const canBrowse = !!props.value.projectSlug && !segmentSelected;

  return (
    <Root>
      <HeaderCopy>{props.value.title ?? 'Ok, great! What segment would you like to chat with?'}</HeaderCopy>
      <PickCopy>Select Segment</PickCopy>
      <Segments>
        <FormGroup>
          {props.value.segments.map((segment) => (
            <FormControlLabel
              key={segment.id}
              control={
                <Checkbox
                  checked={selectedId === segment.id}
                  onChange={(_, checked) => handleCheckboxChange(segment.id, checked)}
                />
              }
              label={segment.name}
            />
          ))}
        </FormGroup>
      </Segments>
      <Actions>
        {/* TODO: Wire up "Chat with segment" navigation before enabling
                {canActOnSegment && (
                  <AnalysisQueryHint
                    value={`Chat with ${selectedSegment!.name}`}
                    onClick={() => undefined}
                  />
                )}
        */}
        {canActOnSegment && (
          <AnalysisQueryHint
            value="Launch Digital Twin"
            onClick={handleLaunchDigitalTwin}
          />
        )}
        {canBrowse && (
          <AnalysisQueryHint
            value="Browse all segments"
            onClick={handleBrowseAll}
          />
        )}
        <CancelAction
          displayValue="Actually, I'd like to do something else."
          onClick={handleCancel}
        />
      </Actions>
    </Root>
  );
};

const Root = styled.div`
  box-sizing: border-box;
`;

const HeaderCopy = styled.div`
  box-sizing: border-box;
  border-bottom: 1px solid ${(props) => props.theme.palette.gray.light1};
  padding-bottom: 10px;
  margin-bottom: 10px;
`;

const PickCopy = styled.div`
  font-size: 15px;
  font-family: ${(props) => props.theme.fonts.semiBold};
  margin-bottom: 10px;
`;

const Segments = styled.div`
  display: flex;
  flex-direction: column;

  &:not(:last-child) {
    margin-bottom: 20px;
  }
`;

const Actions = styled.div`
  margin-top: 10px;

  > :not(:last-child) {
    margin-bottom: 10px;
  }
`;
